
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../course/components/Header';
import Footer from '../course/components/Footer';

interface Resource {
  id: string;
  title: string;
  description: string;
  type: 'pdf' | 'link' | 'checklist' | 'template' | 'video' | 'spreadsheet' | 'guide';
  url: string;
  category: 'forms' | 'guides' | 'business' | 'marketing' | 'legal' | 'financing' | 'tools' | 'regulations';
  isFree: boolean;
  downloadable?: boolean;
  content?: string;
  isPremium?: boolean;
  icon?: string;
}

export default function ResourcesPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showResourceModal, setShowResourceModal] = useState(false);

  const resources: Resource[] = [
    // Forms & Applications (3)
    {
      id: 'dealer-license-checklist',
      title: 'Complete Dealer License Application Checklist',
      description: 'Step-by-step checklist of all 47 documents needed for your Georgia dealer license application',
      type: 'checklist',
      url: '#',
      category: 'forms',
      isFree: true,
      downloadable: true,
      content: `GEORGIA DEALER LICENSE APPLICATION CHECKLIST

BUSINESS FORMATION DOCUMENTS:
☐ Articles of Incorporation or LLC Formation Documents
☐ Business License from Local Municipality
☐ Federal EIN (Employer Identification Number)
☐ Georgia State Tax ID Number
☐ Registered Agent Designation (if applicable)

FINANCIAL DOCUMENTS:
☐ Personal Financial Statement (all owners)
☐ Business Financial Statement (if existing business)
☐ Bank Statements (3 months, personal and business)
☐ Credit Reports (all owners)
☐ Proof of Liquid Assets ($50,000+ recommended)

LOCATION REQUIREMENTS:
☐ Lease Agreement or Property Deed
☐ Zoning Compliance Letter
☐ Certificate of Occupancy
☐ Business Address Verification
☐ Signage Compliance Documentation

INSURANCE & BONDING:
☐ Surety Bond ($35,000 minimum)
☐ Garage Liability Insurance
☐ General Liability Insurance
☐ Workers Compensation (if employees)
☐ Proof of Insurance Coverage

PERSONAL REQUIREMENTS:
☐ Pre-License Education Certificate
☐ Fingerprinting/Background Check
☐ Driver's License (all owners)
☐ Social Security Cards
☐ Passport Photos

APPLICATION FORMS:
☐ Dealer License Application (Form MV-1D)
☐ Individual Application (each owner)
☐ Location Inspection Request
☐ Fee Payment ($300 application fee)

For complete guidance and templates, enroll in our Georgia Dealer License Course.`
    },
    {
      id: 'pre-license-seminar-guide',
      title: 'Pre-License Seminar Registration Guide',
      description: 'Complete guide to scheduling and attending your required pre-license education seminar',
      type: 'guide',
      url: '#',
      category: 'forms',
      isFree: false,
      downloadable: true
    },
    {
      id: 'fingerprinting-checklist',
      title: 'Fingerprinting Process Checklist',
      description: 'Where to go, what to bring, and how to complete your background check requirements',
      type: 'checklist',
      url: '#',
      category: 'forms',
      isFree: false,
      downloadable: true
    },

    // Guides & Tutorials (3)
    {
      id: 'location-requirements-guide',
      title: 'Dealership Location Requirements Guide',
      description: 'Detailed guide on zoning, signage, facility requirements, and location compliance',
      type: 'guide',
      url: '#',
      category: 'guides',
      isFree: false,
      downloadable: true
    },
    {
      id: 'bond-insurance-guide',
      title: 'Bond & Insurance Shopping Guide',
      description: 'How to find the best rates on surety bonds and insurance, including provider recommendations',
      type: 'guide',
      url: '#',
      category: 'guides',
      isFree: true,
      downloadable: true,
      content: `GEORGIA DEALER BOND & INSURANCE GUIDE

SURETY BOND REQUIREMENTS:
• Minimum Amount: $35,000
• Purpose: Protects consumers from dealer fraud
• Cost: Typically 1-3% of bond amount annually
• Required for license approval

RECOMMENDED BOND PROVIDERS:
1. Surety Bonds Direct - (800) 555-0123
2. National Surety Services - (800) 555-0124
3. Georgia Bond Company - (800) 555-0125

GARAGE LIABILITY INSURANCE:
• Minimum Coverage: $100,000 per occurrence
• Covers customer vehicles in your care
• Required for test drives and service work

GENERAL LIABILITY INSURANCE:
• Protects against slip/fall and property damage
• Minimum $1M recommended
• Covers business operations

INSURANCE PROVIDERS:
1. Progressive Commercial - (800) 555-0126
2. State Farm Business - (800) 555-0127
3. Allstate Commercial - (800) 555-0128

MONEY-SAVING TIPS:
• Bundle policies for discounts
• Maintain good credit score
• Complete safety training programs
• Shop quotes from multiple providers

For complete guidance and templates, get our full course.`
    },
    {
      id: 'video-tutorial-series',
      title: 'Georgia Dealer License Video Tutorial Series',
      description: 'Complete 8-module video course walking through every step of the licensing process',
      type: 'video',
      url: '#',
      category: 'guides',
      isFree: false,
      downloadable: true
    },

    // Business Setup (7)
    {
      id: 'business-plan-template',
      title: 'Professional Business Plan Template',
      description: 'Complete business plan template specifically designed for Georgia car dealerships',
      type: 'template',
      url: '#',
      category: 'business',
      isFree: false,
      downloadable: true
    },
    {
      id: 'startup-cost-calculator',
      title: 'Startup Cost Calculator Spreadsheet',
      description: 'Detailed spreadsheet to calculate all startup costs including inventory, facility, and licensing',
      type: 'spreadsheet',
      url: '#',
      category: 'business',
      isFree: false,
      downloadable: true
    },
    {
      id: 'financial-projections',
      title: 'Financial Projections Template',
      description: 'Professional financial projections template with 3-year forecasts and break-even analysis',
      type: 'template',
      url: '#',
      category: 'business',
      isFree: false,
      downloadable: true
    },
    {
      id: 'giada-membership',
      title: 'GIADA - Georgia Independent Automobile Dealers Association',
      description: 'Join Georgia\'s premier independent dealer association for advocacy, training, and industry support',
      type: 'link',
      url: 'https://giada.org/',
      category: 'business',
      isFree: true,
      downloadable: false,
      content: `GIADA - GEORGIA INDEPENDENT AUTOMOBILE DEALERS ASSOCIATION

WHAT IS GIADA?
Georgia Independent Automobile Dealers Association (GIADA) is the state's leading trade association representing independent used car dealers throughout Georgia. Founded to protect and advance the interests of independent dealers, GIADA serves as the unified voice for the industry.

WHY JOIN GIADA?
As an independent dealer in Georgia, GIADA membership provides essential resources, advocacy, and support that can make the difference between struggling and thriving in the automotive industry.

MEMBER BENEFITS:

LEGISLATIVE ADVOCACY:
• Monitors state and local legislation affecting dealers
• Lobbies for dealer-friendly laws and regulations
• Fights against harmful legislation
• Provides updates on regulatory changes
• Represents dealer interests at the state capitol

LEGAL SUPPORT & COMPLIANCE:
• Access to legal consultation services
• Compliance guidance and updates
• Regulatory interpretation assistance
• Legal resource library
• Attorney referral network
• Contract review services

EDUCATION & TRAINING:
• Monthly educational seminars
• Compliance training workshops
• Sales and finance training programs
• New dealer orientation sessions
• Online training resources
• Industry best practices sharing

For complete information about GIADA membership benefits and how to join, visit their website or contact their office directly. GIADA membership is one of the best investments an independent dealer can make for long-term business success.`
    },
    {
      id: 'bizee-incorporation',
      title: 'Bizee Business Incorporation Service',
      description: 'Professional business incorporation service - form your LLC or Corporation quickly and affordably',
      type: 'link',
      url: 'https://www.bizee.com/',
      category: 'business',
      isFree: true,
      downloadable: false,
      content: `BIZEE BUSINESS INCORPORATION SERVICE

WHAT IS BIZEE?
Bizee (formerly IncFile) is a leading business incorporation service that helps entrepreneurs form LLCs, Corporations, and other business entities quickly, affordably, and with professional guidance.

WHY INCORPORATE YOUR DEALERSHIP BUSINESS?
Before applying for your Georgia dealer license, you need to have a properly formed business entity. Most dealers choose either an LLC or Corporation for liability protection and tax benefits.

BIZEE SERVICES OFFERED:
• LLC Formation ($0 + state fees)
• Corporation Formation ($0 + state fees)
• Registered Agent Service (1 year free)
• EIN (Federal Tax ID) Assistance
• Operating Agreement Templates
• Business Banking Setup Assistance
• Compliance Calendar and Reminders

For complete business formation guidance and dealer license requirements, get our full course.`
    },
    {
      id: 'georgia-dor-website',
      title: 'Georgia Department of Revenue - Motor Vehicle Division',
      description: 'Official DOR website with forms, applications, and current regulations for dealer licensing',
      type: 'link',
      url: 'https://dor.georgia.gov/',
      category: 'business',
      isFree: true,
      downloadable: false,
      content: `GEORGIA DEPARTMENT OF REVENUE - MOTOR VEHICLE DIVISION

OFFICIAL GOVERNMENT RESOURCE:
This is the official Georgia Department of Revenue website where you can access all current forms, applications, and regulations for dealer licensing.

AVAILABLE RESOURCES:
• Dealer License Application Forms (MV-1D)
• Current Fee Schedules
• Licensing Requirements and Guidelines
• Regulatory Updates and Changes
• Contact Information for Licensing Staff
• Office Locations and Hours

Always use the official DOR website for the most current and accurate information regarding Georgia dealer licensing requirements.`
    },
    {
      id: 'georgia-sos-business',
      title: 'Georgia Secretary of State Business Registration',
      description: 'Register for state tax accounts and get your Georgia tax ID number online',
      type: 'link',
      url: 'https://sos.ga.gov/corporations',
      category: 'business',
      isFree: true,
      downloadable: false
    },
    {
      id: 'irs-ein-application',
      title: 'IRS EIN Application',
      description: 'Apply for your Federal Employer Identification Number online - required for business operations',
      type: 'link',
      url: 'https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online',
      category: 'business',
      isFree: true,
      downloadable: false
    },

    // Marketing Materials (4)
    {
      id: 'grand-opening-kit',
      title: 'Grand Opening Marketing Kit',
      description: 'Complete marketing materials and strategies for your dealership grand opening',
      type: 'template',
      url: '#',
      category: 'marketing',
      isFree: false,
      downloadable: true
    },
    {
      id: 'social-media-templates',
      title: 'Social Media Templates Pack',
      description: '50+ ready-to-use social media posts, ads, and content templates for Facebook, Instagram, and Google',
      type: 'template',
      url: '#',
      category: 'marketing',
      isFree: false,
      downloadable: true
    },
    {
      id: 'email-marketing-templates',
      title: 'Email Marketing Templates',
      description: 'Welcome sequences, follow-up campaigns, and customer retention email templates',
      type: 'template',
      url: '#',
      category: 'marketing',
      isFree: false,
      downloadable: true
    },
    {
      id: 'print-marketing-materials',
      title: 'Print Marketing Materials',
      description: 'Business cards, flyers, promotional materials, and advertising templates',
      type: 'template',
      url: '#',
      category: 'marketing',
      isFree: false,
      downloadable: true
    },

    // Legal Documents (4)
    {
      id: 'purchase-agreement-templates',
      title: 'Vehicle Purchase Agreement Templates',
      description: 'Georgia-compliant purchase agreement templates with all required disclosures',
      type: 'template',
      url: '#',
      category: 'legal',
      isFree: false,
      downloadable: true
    },
    {
      id: 'warranty-disclosure-forms',
      title: 'Warranty Disclosure Forms',
      description: 'Required warranty disclosure forms and "As Is" sale documentation',
      type: 'template',
      url: '#',
      category: 'legal',
      isFree: false,
      downloadable: true
    },
    {
      id: 'advertising-compliance-guide',
      title: 'Advertising Compliance Guide',
      description: 'Complete guide to Georgia advertising laws and compliant ad examples',
      type: 'guide',
      url: '#',
      category: 'legal',
      isFree: true,
      downloadable: true,
      content: `GEORGIA DEALER ADVERTISING COMPLIANCE GUIDE

REQUIRED DISCLOSURES:
• Dealer license number must appear in all ads
• "Plus tax, tag, title" or similar language required
• Interest rates must include APR if mentioned
• "Subject to credit approval" for financing ads

PROHIBITED PRACTICES:
• False or misleading statements
• Bait and switch advertising
• Hidden fees or charges
• Discriminatory language

VEHICLE PRICING RULES:
• Must honor advertised prices
• Additional fees must be disclosed
• Trade-in allowances must be realistic
• Financing terms must be accurate

For complete compliance templates and examples, get our full course.`
    },
    {
      id: 'consumer-protection-notices',
      title: 'Consumer Protection Notices',
      description: 'Required consumer protection notices and disclosure forms for your dealership',
      type: 'template',
      url: '#',
      category: 'legal',
      isFree: false,
      downloadable: true
    },

    // Financing Guides (4)
    {
      id: 'credit-application-templates',
      title: 'Credit Application Templates',
      description: 'Professional credit application forms and customer qualification templates',
      type: 'template',
      url: '#',
      category: 'financing',
      isFree: false,
      downloadable: true
    },
    {
      id: 'lender-network-guide',
      title: 'Lender Network Building Guide',
      description: 'Complete guide to building relationships with banks, credit unions, and finance companies',
      type: 'guide',
      url: '#',
      category: 'financing',
      isFree: false,
      downloadable: true
    },
    {
      id: 'subprime-lending-guide',
      title: 'Subprime Lending Best Practices',
      description: 'Strategies for working with credit-challenged customers and subprime lenders',
      type: 'guide',
      url: '#',
      category: 'financing',
      isFree: false,
      downloadable: true
    },
    {
      id: 'finance-presentation-scripts',
      title: 'Finance Presentation Scripts',
      description: 'Proven scripts and objection handling techniques for finance presentations',
      type: 'template',
      url: '#',
      category: 'financing',
      isFree: false,
      downloadable: true
    },

    // Tools & Software (10)
    {
      id: 'inventory-management-spreadsheet',
      title: 'Inventory Management Spreadsheet',
      description: 'Professional inventory tracking spreadsheet with profit analysis and aging reports',
      type: 'spreadsheet',
      url: '#',
      category: 'tools',
      isFree: false,
      downloadable: true
    },
    {
      id: 'customer-database-template',
      title: 'Customer Database Template',
      description: 'CRM-style customer database template for tracking leads, sales, and follow-ups',
      type: 'template',
      url: '#',
      category: 'tools',
      isFree: false,
      downloadable: true
    },
    {
      id: 'sales-training-materials',
      title: 'Sales Training Materials',
      description: 'Complete sales training guide including customer types, objection handling, and closing techniques',
      type: 'guide',
      url: '#',
      category: 'tools',
      isFree: false,
      downloadable: true
    },
    {
      id: 'industry-contact-database',
      title: 'Industry Contact Database',
      description: 'Comprehensive database of 100+ industry contacts including auctions, lenders, and vendors',
      type: 'template',
      url: '#',
      category: 'tools',
      isFree: false,
      downloadable: true
    },
    {
      id: 'titletech-platform',
      title: 'TitleTech - Digital Title Management Platform',
      description: 'Revolutionary digital platform for electronic title transfers, lien processing, and DMV integration - streamline your title operations',
      type: 'link',
      url: 'https://www.solera.com/solutions/vehicle-solutions/titling/',
      category: 'tools',
      isPremium: false,
      icon: 'ri-file-shield-2-line',
      downloadable: false,
      content: `WHAT IS TITLETECH?
TitleTech is a revolutionary digital platform by Solera that transforms how automotive dealers handle vehicle titles, liens, and DMV transactions. Instead of dealing with paper titles and manual processes, TitleTech provides a fully digital solution.

KEY FEATURES:
• Electronic Title Transfers - Process title transfers digitally without paper documents
• Automated Lien Processing - Handle lien releases and additions electronically  
• DMV Integration - Direct connection to state DMV systems for real-time updates
• Compliance Management - Ensure all transactions meet state and federal requirements
• Audit Trail - Complete digital record of all title transactions
• Multi-State Support - Works across different state DMV systems

BENEFITS FOR DEALERS:
• Faster Processing - Complete title work in minutes instead of days
• Reduced Errors - Eliminate manual data entry mistakes
• Cost Savings - Reduce administrative overhead and processing costs
• Better Customer Experience - Faster vehicle delivery and title processing
• Compliance Assurance - Built-in compliance checks and reporting

HOW IT WORKS:
1. Digital Integration - Connect your DMS to the TitleTech platform
2. Electronic Processing - Submit title applications and changes digitally
3. Real-Time Updates - Receive instant status updates from DMV systems
4. Automated Workflows - Let the system handle routine title operations
5. Digital Records - Maintain complete electronic records for auditing

GETTING STARTED:
• Contact Solera to discuss implementation
• Integration with your existing dealer management system
• Training for your staff on the digital processes
• Ongoing support and system updates

This platform is essential for modern dealerships looking to streamline their title operations and improve efficiency.`
    },
    {
      id: 'auction-access-platform',
      title: 'Auction Access - Wholesale Vehicle Marketplace',
      description: 'Digital wholesale marketplace connecting dealers with nationwide vehicle auctions, inventory sourcing, and wholesale opportunities',
      type: 'link',
      url: 'https://auctionaccess.com/',
      category: 'tools',
      isFree: true,
      downloadable: false,
      content: `AUCTION ACCESS - WHOLESALE VEHICLE MARKETPLACE

PLATFORM OVERVIEW:
Auction Access is a comprehensive digital marketplace that connects automotive dealers with wholesale vehicle auctions, inventory sourcing opportunities, and wholesale trading networks across the United States.

KEY FEATURES:
• Access to nationwide vehicle auctions
• Real-time inventory sourcing
• Wholesale marketplace connections
• Digital bidding and purchasing tools
• Inventory management integration

For complete wholesale sourcing strategies and auction guidance, get our full course.`
    },
    {
      id: 'nada-dealer-resources',
      title: 'NADA Dealer Resources',
      description: 'National Automobile Dealers Association resources, tools, and industry data',
      type: 'link',
      url: 'https://www.nada.org/',
      category: 'tools',
      isFree: true
    },
    {
      id: 'kbb-dealer-tools',
      title: 'Kelley Blue Book Dealer Tools',
      description: 'Vehicle valuation and market analysis tools for automotive dealers',
      type: 'link',
      url: 'https://www.kbb.com/dealers/',
      category: 'tools',
      isFree: true
    },
    {
      id: 'autotrader-dealer',
      title: 'AutoTrader Dealer Solutions',
      description: 'Online marketplace and advertising solutions for automotive dealers',
      type: 'link',
      url: 'https://www.autotrader.com/car-dealers/',
      category: 'tools',
      isFree: true,
      downloadable: false,
      content: `AUTOTRADER DEALER SOLUTIONS

PLATFORM OVERVIEW:
AutoTrader is one of the largest online automotive marketplaces, connecting dealers with millions of car shoppers every month.

DEALER SERVICES:
• Vehicle Listing and Inventory Management
• Lead Generation and Customer Connections
• Digital Marketing and Advertising Solutions
• Market Analytics and Pricing Tools
• Mobile-Optimized Dealer Pages
• Integration with Dealer Management Systems

For complete digital marketing strategies and online presence guidance, get our full course.`
    }
  ];

  const categories = [
    { id: 'all', name: 'All Resources', icon: 'ri-folder-open-line' },
    { id: 'forms', name: 'Forms & Applications', icon: 'ri-file-list-line' },
    { id: 'guides', name: 'Guides & Tutorials', icon: 'ri-book-open-line' },
    { id: 'business', name: 'Business Setup', icon: 'ri-building-line' },
    { id: 'marketing', name: 'Marketing Materials', icon: 'ri-megaphone-line' },
    { id: 'legal', name: 'Legal Documents', icon: 'ri-scales-line' },
    { id: 'financing', name: 'Financing Guides', icon: 'ri-bank-line' },
    { id: 'tools', name: 'Tools & Software', icon: 'ri-tools-line' }
  ];

  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(resource => resource.category === selectedCategory);

  const handleResourceClick = (resource: Resource) => {
    setSelectedResource(resource);
    
    // Special handling for TitleTech platform
    if (resource.id === 'titletech-platform') {
      try {
        console.log('Opening TitleTech (Solera) website:', 'https://www.solera.com/solutions/vehicle-solutions/titling/');
        
        // Enhanced link opening with multiple fallback methods
        const openLink = () => {
          const url = 'https://www.solera.com/solutions/vehicle-solutions/titling/';
          
          // Method 1: Try opening in new tab
          const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
          
          // Check if popup was blocked
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            console.log('Popup blocked, trying direct navigation...');
            // Method 2: Direct navigation as fallback
            window.location.href = url;
            return false;
          }
          
          return true;
        };
        
        const success = openLink();
        
        if (success) {
          showNotification('Opening TitleTech (Solera) Website', 'success');
        }
        
      } catch (error) {
        console.error('Error opening TitleTech link:', error);
        
        // Show error with manual URL and retry button
        const errorDiv = document.createElement('div');
        errorDiv.className = 'fixed top-4 left-4 right-4 bg-red-50 border-2 border-red-200 rounded-xl shadow-lg z-50 p-4 max-w-md mx-auto';
        errorDiv.innerHTML = `
          <div class="flex items-start">
            <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <i class="ri-error-warning-line text-red-500"></i>
            </div>
            <div class="flex-1">
              <h4 class="font-bold text-red-900 mb-2">Link Opening Failed</h4>
              <p class="text-red-700 text-sm mb-3">Unable to open TitleTech automatically. Please visit:</p>
              <p class="text-blue-600 text-sm font-mono mb-3 break-all">https://www.solera.com/solutions/vehicle-solutions/titling/</p>
              <button onclick="window.open('https://www.solera.com/solutions/vehicle-solutions/titling/', '_blank')" 
                      class="bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors text-sm mr-2 whitespace-nowrap">
                Try Again
              </button>
            </div>
            <button onclick="this.parentElement.parentElement.remove()" 
                    class="text-red-500 hover:text-red-700 ml-2 flex-shrink-0">
              <i class="ri-close-line"></i>
            </button>
          </div>
        `;
        document.body.appendChild(errorDiv);
        
        // Auto-remove after 8 seconds
        setTimeout(() => {
          if (errorDiv.parentElement) {
            errorDiv.remove();
          }
        }, 8000);
      }
      return;
    }

    // Special handling for Auction Access platform
    if (resource.id === 'auction-access-platform') {
      const openAuctionAccess = () => {
        try {
          // Use the correct Auction Access URL
          const auctionAccessUrl = 'https://auctionaccess.com/';
          
          console.log('Opening Auction Access URL:', auctionAccessUrl);
          
          // Method 1: Try opening in new tab
          const newWindow = window.open(auctionAccessUrl, '_blank', 'noopener,noreferrer');
          
          // Check if popup was blocked
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            console.log('Popup blocked, trying direct navigation');
            // Method 2: Direct navigation as fallback
            window.location.href = auctionAccessUrl;
            return;
          }
          
          // Success notification
          showSuccessToast('Opening Auction Access Website');
          
        } catch (error) {
          console.error('Error opening Auction Access link:', error);
          
          // Show error with manual URL
          const errorToast = document.createElement('div');
          errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 max-w-md';
          errorToast.innerHTML = `
            <div class="flex flex-col space-y-3">
              <div class="flex items-center space-x-2">
                <i class="ri-error-warning-line"></i>
                <span class="font-semibold">Link Opening Failed</span>
              </div>
              <div class="text-sm">
                Please visit manually: <strong>auctionaccess.com</strong>
              </div>
              <button onclick="window.open('https://auctionaccess.com/', '_blank'); this.parentElement.parentElement.remove();" 
                      class="bg-white text-red-600 px-3 py-1 rounded text-sm font-medium hover:bg-red-50 transition-colors">
                Try Again
              </button>
            </div>
          `;
          document.body.appendChild(errorToast);
          
          // Auto-remove after 8 seconds
          setTimeout(() => {
            if (document.body.contains(errorToast)) {
              document.body.removeChild(errorToast);
            }
          }, 8000);
        }
      };
      
      openAuctionAccess();
      return;
    }

    // Handle other external links
    if (resource.type === 'link' && resource.url && resource.url !== '#') {
      try {
        console.log('Opening external link:', resource.url);
        
        // Method 1: Try opening in new tab
        const newWindow = window.open(resource.url, '_blank', 'noopener,noreferrer');
        
        // Check if popup was blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          console.log('Popup blocked, trying direct navigation');
          // Method 2: Direct navigation as fallback
          window.location.href = resource.url;
          return;
        }
        
        // Success notification
        showSuccessToast(`Opening ${resource.title}`);
        
      } catch (error) {
        console.error('Error opening external link:', error);
        showErrorToast('Unable to open link automatically', `Please visit: ${resource.url}`);
      }
      return;
    }

    // For downloadable content, show modal with content
    if (resource.content) {
      setShowResourceModal(true);
    }
  };

  const showSuccessToast = (message: string) => {
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    toast.innerHTML = `
      <div class="flex items-center space-x-2">
        <i class="ri-external-link-line"></i>
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 3000);
  };

  const showErrorToast = (title: string, subtitle: string) => {
    const errorToast = document.createElement('div');
    errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    errorToast.innerHTML = `
      <div class="flex flex-col space-y-2">
        <div class="flex items-center space-x-2">
          <i class="ri-error-warning-line"></i>
          <span>${title}</span>
        </div>
        <div class="text-sm">
          ${subtitle}
        </div>
      </div>
    `;
    document.body.appendChild(errorToast);
    setTimeout(() => {
      if (document.body.contains(errorToast)) {
        document.body.removeChild(errorToast);
      }
    }, 5000);
  };

  const handleDownloadResource = (resource: Resource) => {
    if (!resource.content) {
      showErrorToast('No content available', 'This resource requires course purchase');
      return;
    }

    try {
      const content = `${resource.title.toUpperCase()}\n\n${resource.content}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resource.title.replace(/[^a-zA-Z0-9]/g, '-')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccessToast(`Downloaded: ${resource.title}`);
    } catch (error) {
      console.error('Download error:', error);
      showErrorToast('Download failed', 'Please try again');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-folder-open-line text-2xl text-blue-600"></i>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Complete Resource Library
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to successfully obtain your Georgia used car dealer license - templates, guides, forms, and expert resources
            </p>
          </div>

          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-yellow-500 text-black'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${category.icon} text-sm`}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Resources Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {filteredResources.map((resource) => (
              <div key={resource.id} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    resource.type === 'pdf' ? 'bg-red-100' :
                    resource.type === 'link' ? 'bg-blue-100' :
                    resource.type === 'checklist' ? 'bg-green-100' :
                    resource.type === 'template' ? 'bg-purple-100' :
                    resource.type === 'video' ? 'bg-orange-100' :
                    resource.type === 'spreadsheet' ? 'bg-emerald-100' :
                    'bg-gray-100'
                  }`}>
                    <i className={`${
                      resource.type === 'pdf' ? 'ri-file-pdf-line text-red-600' :
                      resource.type === 'link' ? 'ri-external-link-line text-blue-600' :
                      resource.type === 'checklist' ? 'ri-checkbox-line text-green-600' :
                      resource.type === 'template' ? 'ri-file-text-line text-purple-600' :
                      resource.type === 'video' ? 'ri-video-line text-orange-600' :
                      resource.type === 'spreadsheet' ? 'ri-file-excel-line text-emerald-600' :
                      'ri-file-line text-gray-600'
                    } text-xl`}></i>
                  </div>
                  <div className="flex items-center space-x-2">
                    {resource.isFree && (
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                        FREE
                      </span>
                    )}
                    {!resource.isFree && (
                      <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                        PREMIUM
                      </span>
                    )}
                  </div>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                  {resource.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {resource.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 capitalize">
                    {resource.category.replace('-', ' ')}
                  </span>
                  <div className="flex space-x-2">
                    {resource.content && (
                      <button
                        onClick={() => handleResourceClick(resource)}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
                      >
                        Preview
                      </button>
                    )}
                    {resource.type === 'link' ? (
                      <button
                        onClick={() => handleResourceClick(resource)}
                        className="bg-yellow-500 text-black px-3 py-1 rounded-lg text-xs font-medium hover:bg-yellow-400 transition-colors whitespace-nowrap"
                      >
                        Visit
                      </button>
                    ) : (
                      <button
                        onClick={() => resource.isFree ? handleDownloadResource(resource) : navigate('/purchase')}
                        className="bg-yellow-500 text-black px-3 py-1 rounded-lg text-xs font-medium hover:bg-yellow-400 transition-colors whitespace-nowrap"
                      >
                        {resource.isFree ? 'Download' : 'Get Access'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Value Proposition */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">$497+ Value in Professional Resources</h3>
            <p className="text-blue-100 text-lg mb-6">
              Get instant access to all templates, guides, and resources with our complete course
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button 
                onClick={() => window.open('https://www.georgiadealerlicense.com/purchase', '_blank')}
                className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Complete Access - $97
              </button>
              <a 
                href="https://drive.google.com/file/d/15mwTL1E347Xo6zRAAb_miL0HbDsxiOHn/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap"
              >
                Browse All Resources
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Resource Modal */}
      {showResourceModal && selectedResource && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedResource.title}</h3>
                <p className="text-gray-600">{selectedResource.description}</p>
              </div>
              <button
                onClick={() => setShowResourceModal(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                {selectedResource.content}
              </pre>
            </div>
            
            <div className="p-6 bg-gray-50 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedResource.isFree ? 'Free Resource' : 'Premium Resource - Course Required'}
                </span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowResourceModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Close
                  </button>
                  {selectedResource.isFree ? (
                    <button
                      onClick={() => {
                        handleDownloadResource(selectedResource);
                        setShowResourceModal(false);
                      }}
                      className="bg-yellow-500 text-black px-6 py-2 rounded-lg font-medium hover:bg-yellow-400 transition-colors whitespace-nowrap"
                    >
                      Download
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/purchase')}
                      className="bg-yellow-500 text-black px-6 py-2 rounded-lg font-medium hover:bg-yellow-400 transition-colors whitespace-nowrap"
                    >
                      Get Full Access
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
